import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PermissionGate } from "@/components/common/PermissionGate";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Edit, MoreVertical, Trash2, Layers } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import type {
  ComponentPriceListDto,
  BillingFrequency,
} from "@/types/component-price";
import { useDeleteComponentPrice } from "@/hooks/useComponentPrices";

// ============================================================================
// Types
// ============================================================================

interface ComponentPriceTableProps {
  prices: ComponentPriceListDto[];
  isLoading?: boolean;
  onEdit?: (price: ComponentPriceListDto) => void;
  onViewTiers?: (price: ComponentPriceListDto) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getBillingFrequencyBadgeVariant(
  frequency: BillingFrequency,
): "default" | "secondary" | "outline" {
  switch (frequency) {
    case "Hourly":
      return "default";
    case "Daily":
      return "secondary";
    case "Monthly":
      return "outline";
    default:
      return "outline";
  }
}

// ============================================================================
// Component
// ============================================================================

export function ComponentPriceTable({
  prices,
  isLoading = false,
  onEdit,
  onViewTiers,
}: ComponentPriceTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] =
    useState<ComponentPriceListDto | null>(null);
  const deleteMutation = useDeleteComponentPrice();

  const handleDeleteClick = (price: ComponentPriceListDto) => {
    setSelectedPrice(price);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedPrice) {
      await deleteMutation.mutateAsync(selectedPrice.id);
      setDeleteDialogOpen(false);
      setSelectedPrice(null);
    }
  };

  const isCurrentlyEffective = (
    effectiveFrom: string,
    effectiveTo?: string,
  ) => {
    const now = new Date();
    const from = new Date(effectiveFrom);
    const to = effectiveTo ? new Date(effectiveTo) : null;

    return from <= now && (!to || to >= now);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (prices.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No component prices found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Create a new price to get started
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Region</TableHead>
              <TableHead>Price/Unit</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Billing Frequency</TableHead>
              <TableHead>Effective From</TableHead>
              <TableHead>Effective To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prices.map((price) => (
              <TableRow key={price.id}>
                <TableCell>
                  {price.region ? (
                    <span className="font-medium">{price.region}</span>
                  ) : (
                    <span className="text-muted-foreground italic">Global</span>
                  )}
                </TableCell>
                <TableCell className="font-mono font-semibold">
                  {formatCurrency(price.pricePerUnit, price.currency)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{price.currency}</Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={getBillingFrequencyBadgeVariant(
                      price.billingFrequency,
                    )}
                  >
                    {price.billingFrequency}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(price.effectiveFrom)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {price.effectiveTo ? (
                    formatDate(price.effectiveTo)
                  ) : (
                    <span className="italic">No end date</span>
                  )}
                </TableCell>
                <TableCell>
                  {price.isActive &&
                  isCurrentlyEffective(
                    price.effectiveFrom,
                    price.effectiveTo,
                  ) ? (
                    <Badge variant="default" className="bg-ok">
                      Active
                    </Badge>
                  ) : price.isActive ? (
                    <Badge variant="secondary">Scheduled</Badge>
                  ) : (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                        {onEdit && (
                          <DropdownMenuItem onClick={() => onEdit(price)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                      </PermissionGate>
                      {onViewTiers && (
                        <DropdownMenuItem onClick={() => onViewTiers(price)}>
                          <Layers className="h-4 w-4 mr-2" />
                          View Tiers
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(price)}
                          className="text-destructive-ink"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </PermissionGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Component Price</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to delete this price for{" "}
              {selectedPrice?.componentName}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
